import * as Tooltip from "@radix-ui/react-tooltip";
import { DeviceDeleteTooltip } from "../../style/contents/backup/backup";
import { downloadS3File } from "../../util/download.s3";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ImageBackupFileItem } from "../../view/contents/image/image.drive";
import { NewTableContainer } from "../../style/contents/image/image";
import Pagination from "../common/table.pagination";
import { useLocation, useNavigate } from "react-router-dom";
import {
  fetchRequestDownloadDiskBackup,
  fetchRequestWormDirectory,
  fetchRequestWormFile,
} from "../../api/admin";
import ModalCenterLayout from "../modal/modal.center.layout";

interface ImageDriveListTableProps {
  data: ImageBackupFileItem[];
  searchKeyword?: string;
  pageSize: number;
  paginationEnabled?: boolean;
  navigationSearch?: string;
  onOpenImage?: (file: ImageBackupFileItem, directoryPath: string) => void;
  onFolderClick?: (path: string) => void;
  isLoading?: boolean;
  userId: string;
  loginToken: string;
}

export interface PaginationState {
  pageIndex: number;
  pageSize: number;
}

const formatFileSize = (size: number) => {
  if (!size) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const unitIndex = Math.min(
    Math.floor(Math.log(size) / Math.log(1024)),
    units.length - 1,
  );
  const value = size / 1024 ** unitIndex;

  return `${value.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const formatDate = (value: string | number) => {
  if (!value) return "-";

  const timestamp =
    typeof value === "number" || /^\d+$/.test(String(value))
      ? Number(value)
      : value;
  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) return String(value);

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const toSearchText = (file: ImageBackupFileItem) =>
  [
    file.fileName,
    file.type,
    file.size,
    file.rootDirectoryPath,
    formatFileSize(Number(file.size)),
    formatDate(file.createdDate),
  ]
    .map((value) => String(value ?? "").toLowerCase())
    .join(" ");

const getRootDirectoryPath = (file: ImageBackupFileItem) => {
  const rootPath = file.rootDirectoryPath || file.fileName;

  if (!rootPath) return "";
  if (/^[a-zA-Z]:$/.test(rootPath)) return `${rootPath}/`;

  return rootPath;
};

const ImageDriveListTable = ({
  data,
  searchKeyword = "",
  pageSize,
  paginationEnabled = true,
  navigationSearch,
  onOpenImage,
  onFolderClick,
  isLoading,
  userId,
  loginToken,
}: ImageDriveListTableProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });
  const [wormTargetDirectory, setWormTargetDirectory] =
    useState<ImageBackupFileItem | null>(null);
  const [wormModal, setWormModal] = useState({
    isOpen: false,
    isVisible: false,
  });
  const queryString = navigationSearch ?? location.search;

  const handleOpenWormModal = useCallback((file: ImageBackupFileItem) => {
    setWormTargetDirectory(file);
    setWormModal({ isOpen: true, isVisible: true });
  }, []);

  const handleCloseWormModal = useCallback(() => {
    setWormTargetDirectory(null);
    setWormModal({ isOpen: false, isVisible: false });
  }, []);

  const handleRequestWormDirectory = useCallback(async () => {
    if (!userId || !loginToken) {
      alert("사용자 또는 로그인 토큰 정보가 없습니다.");
      return;
    }

    if (!wormTargetDirectory) {
      alert("WORM 요청할 폴더 정보가 없습니다.");
      return;
    }

    const directoryPath = getRootDirectoryPath(wormTargetDirectory);

    if (!directoryPath) {
      alert("WORM 요청할 폴더 경로 정보가 없습니다.");
      return;
    }

    const params = {
      deviceId: wormTargetDirectory.deviceId,
      directoryPath,
    };

    try {
      console.log("[ImageDriveList WORM] directory request params", {
        userId,
        ...params,
      });

      if (
        wormTargetDirectory.contentEntry &&
        wormTargetDirectory.type === "file"
      ) {
        const { fileId, backupHistoryId } = wormTargetDirectory;
        if (fileId == null || backupHistoryId == null) {
          alert("파일 WORM 요청에 필요한 식별 정보가 없습니다.");
          return;
        }
        await fetchRequestWormFile(
          userId,
          { fileId, backupHistoryId },
          loginToken,
        );
      } else {
        await fetchRequestWormDirectory(userId, params, loginToken);
      }

      alert("WORM 요청이 접수되었습니다.");
      handleCloseWormModal();
    } catch (err) {
      console.error("WORM 디렉토리 요청 실패:", err);
      alert("WORM 요청 중 오류가 발생했습니다.");
    }
  }, [handleCloseWormModal, loginToken, userId, wormTargetDirectory]);

  const handleDownloadClick = useCallback(async (file: ImageBackupFileItem) => {
    if (file.contentEntry) {
      downloadS3File(file.s3Url, file.fileName);
      return;
    }
    const adminUserId = localStorage.getItem("userId");

    if (!adminUserId) {
      alert("관리자 정보가 없습니다.");
      return;
    }

    const params = {
      targetUserId: file.userId,
      targetDeviceId: file.deviceId,
      diskBackupStamp: file.backupStamp,
    };

    try {
      await fetchRequestDownloadDiskBackup(adminUserId, params);
      alert("다운로드 요청이 접수되었습니다.");
    } catch (err) {
      console.error("디스크 백업 다운로드 요청 실패:", err);
      alert("다운로드 요청 중 오류가 발생했습니다.");
    }
  }, []);

  const columns = useMemo<ColumnDef<ImageBackupFileItem>[]>(
    () => [
      {
        header: "종류",
        accessorKey: "type",
        cell: (info) => {
          const type = info.getValue();
          if (type === "disk") return "드라이브 이미지";
          if (type === "image") return "이미지";
          if (type === "folder" || type === "directory") return "폴더";
          if (type === "file") return "파일";
          return type ? String(type) : "-";
        },
        size: 160,
      },
      {
        header: "이름",
        accessorKey: "fileName",
        cell: ({ row, getValue }) => {
          const fileName = getValue();
          const rootDirectoryPath = getRootDirectoryPath(row.original);
          if (row.original.contentEntry) {
            const isFolder =
              row.original.type === "folder" ||
              row.original.type === "directory";
            return isFolder ? (
              <span
                className="clickable-folder"
                onClick={() => onFolderClick?.(rootDirectoryPath)}
              >
                {String(fileName || "-")}
              </span>
            ) : (
              <span>{String(fileName || "-")}</span>
            );
          }
          const params = new URLSearchParams(queryString);

          params.set("targetUserId", String(row.original.userId));
          params.set("targetDeviceId", row.original.deviceId);
          params.set("diskBackupStamp", row.original.backupStamp);

          return (
            <span
              className="clickable-folder"
              onClick={() => {
                if (!rootDirectoryPath) {
                  alert("루트 경로 정보가 없습니다.");
                  return;
                }

                if (onOpenImage) {
                  onOpenImage(row.original, rootDirectoryPath);
                  return;
                }
                params.set("directoryPath", rootDirectoryPath);
                navigate(`/image/contents?${params.toString()}`);
              }}
            >
              {fileName ? String(fileName) : "-"}
            </span>
          );
        },
        size: 400,
      },
      {
        header: "크기",
        accessorKey: "size",
        cell: (info) => formatFileSize(Number(info.getValue())),
        size: 140,
      },
      {
        header: "백업날짜",
        accessorKey: "createdDate",
        cell: (info) => formatDate(info.getValue() as string | number),
        size: 220,
      },
      {
        header: "WORM",
        id: "worm",
        cell: ({ row }) => {
          const item = row.original;

          const missingFileIds =
            item.contentEntry &&
            item.type === "file" &&
            (item.fileId == null || item.backupHistoryId == null);
          if (item.isWorm) return "적용됨";

          return (
            <button
              type="button"
              className="worm-request-btn"
              disabled={!!missingFileIds}
              title={
                missingFileIds
                  ? "파일 WORM에 필요한 fileId와 backupHistoryId가 없습니다."
                  : undefined
              }
              onClick={(event) => {
                event.stopPropagation();
                handleOpenWormModal(item);
              }}
            >
              WORM
            </button>
          );
        },
        size: 120,
      },
      {
        header: "다운로드",
        id: "download",
        cell: ({ row }) => (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <span
                style={{ display: "inline-flex" }}
                tabIndex={
                  row.original.contentEntry &&
                  !/^https?:\/\//.test(row.original.s3Url || "")
                    ? 0
                    : undefined
                }
              >
                <button
                  type="button"
                  className="download-btn"
                  aria-label="다운로드"
                  disabled={
                    !!row.original.contentEntry &&
                    !/^https?:\/\//.test(row.original.s3Url || "")
                  }
                  title={
                    row.original.contentEntry &&
                    !/^https?:\/\//.test(row.original.s3Url || "")
                      ? "다운로드 URL이 없습니다."
                      : undefined
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDownloadClick(row.original);
                  }}
                >
                  <img src="/assets/new_design/download_icon.png" alt="" />
                </button>
              </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <DeviceDeleteTooltip
                side="top"
                sideOffset={6}
                collisionPadding={8}
              >
                다운로드
                <Tooltip.Arrow fill="#333333" />
              </DeviceDeleteTooltip>
            </Tooltip.Portal>
          </Tooltip.Root>
        ),
        size: 120,
      },
    ],
    [
      handleDownloadClick,
      handleOpenWormModal,
      navigate,
      queryString,
      onOpenImage,
      onFolderClick,
    ],
  );

  const filteredData = useMemo(() => {
    const keyword = searchKeyword.trim().toLowerCase();
    if (!keyword) return data;

    return data.filter((file) => toSearchText(file).includes(keyword));
  }, [data, searchKeyword]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      pagination,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: paginationEnabled
      ? getPaginationRowModel()
      : undefined,
    onPaginationChange: setPagination,
  });

  const totalRowCount = table.getRowCount();

  useEffect(() => {
    table.setPageSize(pageSize);
  }, [table, pageSize, filteredData]);

  useEffect(() => {
    table.setPageIndex(0);
  }, [table, searchKeyword, filteredData]);

  return (
    <Tooltip.Provider delayDuration={200}>
      <NewTableContainer>
        <div className="table-head-wrap">
          <div className="left">
            <span className="table-total-row">전체 {totalRowCount}</span>
          </div>
        </div>
        <div className="fixed-height">
          <table>
            <thead>
              {table.getHeaderGroups().map((item) => (
                <tr key={item.id}>
                  {item.headers.map((header) => (
                    <th
                      key={header.id}
                      style={{ width: `${header.getSize()}px` }}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={columns.length} className="loading">
                    데이터를 불러오는 중입니다...
                  </td>
                </tr>
              ) : table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="no-data-row">
                    검색 결과가 없습니다.
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {paginationEnabled && table.getPageCount() > 0 && (
          <Pagination
            page={pagination.pageIndex}
            pages={table.getPageCount()}
            onPageChange={(page) => {
              table.setPageIndex(page);
            }}
          />
        )}
        {wormModal.isOpen && (
          <ModalCenterLayout
            isVisible={wormModal.isVisible}
            closedHandler={handleCloseWormModal}
            size="s"
          >
            <div className="worm-confirm-modal">
              <div className="modal-header">
                <h3>WORM 기능 적용</h3>
                <button
                  type="button"
                  className="closed-modal-btn"
                  onClick={handleCloseWormModal}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  적용하시겠습니까? <br /> worm 기능이 적용되면 이미 저장된
                  데이터의 수정/삭제가 불가능하게 됩니다.
                </p>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel"
                  onClick={handleCloseWormModal}
                >
                  취소
                </button>
                <button
                  type="button"
                  className="confirm"
                  onClick={handleRequestWormDirectory}
                >
                  적용
                </button>
              </div>
            </div>
          </ModalCenterLayout>
        )}
      </NewTableContainer>
    </Tooltip.Provider>
  );
};

export default ImageDriveListTable;
